<script>
export default {
  name: 'ThvButton',
  props: {
    size: {
      type: String,
      default: 'medium',
      validator: value => {
        return ['small', 'medium', 'large'].includes(value)
      }
    },
    element: {
      type: String,
      default: 'a',
      validator: value => {
        return ['a', 'button', 'nuxtLink', 'routerLink'].includes(value)
      }
    },
    type: {
      type: String,
      default: 'navy',
      validator: value => {
        return ['navy', 'transparent'].includes(value)
      }
    },
    link: {
      type: [String, Object],
      default: '#'
    },
    colour: {
      type: String,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    target: {
      type: String,
      required: false,
      default: '',
      validator: value => {
        return ['_blank', '', '_top'].includes(value)
      }
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    nativeType: {
      type: String,
      default: 'submit',
      validator: value => {
        return ['button', 'submit', 'reset'].includes(value)
      }
    }
  },
  computed: {
    classes() {
      return [
        this.colourModifierClass,
        this.type,
        this.widthModifierClass,
        {
          'tw-text-sm tw-py-1.5 tw-px-6': this.size === 'small',
          'tw-py-2 tw-px-7': this.size === 'medium',
          'tw-py-3 tw-px-8': this.size === 'large'
        }
      ]
    },
    colourModifierClass() {
      return this.colour ? `button--${this.colour}` : ''
    },
    widthModifierClass() {
      return this.fullWidth ? 'button--full-width' : ''
    }
  }
}
</script>

<template lang="pug">
span
  a.button(
    v-if='element === "a"'
    :class='classes'
    :href='link'
    :target='target'
  )
    slot(name='icon')
    slot

  button.button(
    :disabled='disabled'
    v-else-if='element === "button"'
    :class='classes'
    @click='$emit("click")'
    :target='target'
    :type='nativeType'
  )
    slot(name='icon')
    slot

  nuxt-link.button(
    v-else-if='element === "nuxtLink"'
    :class='classes'
    :to='link'
    :target='target'
  )
    slot(name='icon')
    slot

  router-link.button(
    v-else-if='element === "routerLink"'
    :class='classes'
    :to='link'
    :target='target'
    @click.native='$emit("click")'
  )
    slot(name='icon')
    slot
</template>

<style lang="stylus">
@import '../../assets/styles/modules/*'
a.button,
button.button
  position: relative;
  background-color: THRIVA.colours.navy
  border: 2px solid THRIVA.colours.navy
  border-radius: rem-calc(100px)
  font-family: THRIVA.global.font-family
  font-weight: THRIVA.typography.font-weights.regular
  min-width: 105px
  color: THRIVA.colours.white
  margin: 0
  display: inline-block
  transition: 0.2s
  text-align: center
  outline: none
  cursor pointer
  text-decoration none
  &--full-width
    width: 100%
  &:hover
    +media-breakpoint-up(md)
      transform: translateY(-3px)
      border: 2px solid lighten(THRIVA.colours.navy, 10%)
      box-shadow: 0 1px 4px 0 rgba(THRIVA.colours.carbon,0.18)
      color: THRIVA.colours.white
      position: relative
      &:before
        content: ''
        height: 10px
        width: 100%
        background: transparent
        position: absolute
        bottom: -3px
        left: 0
  &:active
    transform: translateY(0)
    background-color: darken(THRIVA.colours.navy, 30%)
  &:focus
    &:active
      &:before
        display: none
  &:focus
    &:after
      content: ''
      position: absolute
      border-radius: rem-calc(100px)
      top: n = -3px
      right: n
      bottom: n
      left: n
      border: 3px solid THRIVA.colours.lagoon
  &.disabled, &:disabled
    cursor: not-allowed
    background-color: THRIVA.colours.cloud
    color: THRIVA.colours.storm
    border: 2px solid THRIVA.colours.cloud
    &:hover, &:active, &:focus
      transform: translateY(0)
      box-shadow: none
  &--secondary
    background-color: THRIVA.colours.storm
    border: 2px solid THRIVA.colours.storm
    &:hover
      background-color: lighten(THRIVA.colours.storm, 10%)
      border: 2px solid lighten(THRIVA.colours.storm, 10%)
    &:active
      background-color: darken(THRIVA.colours.storm, 15%)
      border: 2px solid darken(THRIVA.colours.storm, 15%)
    &:focus
      &:after
        box-shadow: 0 0 rem-calc(6px) 0 THRIVA.colours.storm
  for colour in THRIVA.colours
    &--{colour}
      background-color: THRIVA.colours[colour]
      border: 2px solid @background-color
      &:hover
        border: 2px solid lighten(@background-color, 10%)
      &:active
        background-color: darken(@background-color, 30%)
        border: 2px solid darken(@background-color, 30%)

      &:disabled
        background-color: THRIVA.colours.cloud
        border: 2px solid THRIVA.colours.cloud
  &--static:hover
    transform: none
    box-shadow: none
  &.transparent, &--white
    background-color: THRIVA.colours.white
    border: 2px solid THRIVA.colours.pebble
    color: THRIVA.colours.carbon
    &:hover
      color: THRIVA.colours.carbon

    &:disabled
      color: THRIVA.colours.storm
      &:hover
        color: THRIVA.colours.storm
      &:before
        display: none
</style>
